import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    createDraftLabRequest,
    downloadLabRequest,
    sendLabRequest,
    signLabRequest,
    syncClientClinicalData,
    syncPracticeLevelPrescriptions,
    syncPrescriptionData,
    triggerClientSync,
    triggerSSOLaunch,
    updateLabRequest,
} from "../services/e-prescribe.services";
import { IApiResponse } from "../../../utils/types/api-response";
import {
    ICreateDraftLabRequestPayload,
    IEditDraftLabRequestPayload,
    ILabRequest,
    ILabRequestMutationResponse,
    ISendLabRequestPayload,
    ISignLabRequestPayload,
    ISSOLaunchPayload,
    ISSOLaunchResponse,
    ISyncClientClinicalDataResponse,
    ISyncPrescriptionDataPayload,
    ISyncPrescriptionDataResponse,
    ITriggerClientSyncPayload,
    ITriggerClientSyncResponse,
} from "../types/e-prescribe.types";
import useToast from "../../../hooks/useToast";

export function useTriggerSSOLaunch() {
    return useMutation<
        IApiResponse<ISSOLaunchResponse>,
        AxiosError<IApiResponse<ISSOLaunchResponse>>,
        { practiceId: string; payload?: ISSOLaunchPayload }
    >({
        mutationKey: ["trigger-sso-launch"],
        mutationFn: ({ practiceId, payload }) =>
            triggerSSOLaunch(practiceId, payload),
    });
}

export function useTriggerClientSync() {
    return useMutation<
        IApiResponse<ITriggerClientSyncResponse>,
        AxiosError<IApiResponse<ITriggerClientSyncResponse>>,
        {
            payload: ITriggerClientSyncPayload;
            practiceId: string;
            clientId: string;
        }
    >({
        mutationKey: ["trigger-client-sync"],
        mutationFn: ({ payload, practiceId, clientId }) =>
            triggerClientSync(practiceId, clientId, payload),
    });
}

export function useSyncClientClinicalData() {
    return useMutation<
        IApiResponse<ISyncClientClinicalDataResponse>,
        AxiosError<IApiResponse<ISyncClientClinicalDataResponse>>,
        { practiceId: string; clientId: string }
    >({
        mutationKey: ["sync-client-clinical-data"],
        mutationFn: ({ practiceId, clientId }) =>
            syncClientClinicalData(practiceId, clientId),
    });
}

export function useSyncPrescriptionData() {
    return useMutation<
        IApiResponse<ISyncPrescriptionDataResponse>,
        AxiosError<IApiResponse<ISyncPrescriptionDataResponse>>,
        {
            practiceId: string;
            clientId: string;
            payload: ISyncPrescriptionDataPayload;
        }
    >({
        mutationKey: ["sync-prescription-data"],
        mutationFn: ({ practiceId, clientId, payload }) =>
            syncPrescriptionData(practiceId, clientId, payload),
    });
}

export function useSyncPracticeLevelPrescriptions(practiceId: string) {
    const { toast } = useToast();
    return useMutation<
        IApiResponse<{ message_id: string }>,
        AxiosError<IApiResponse<{ message_id: string }>>
    >({
        mutationFn: () => syncPracticeLevelPrescriptions(practiceId),
        onError: (err) => {
            toast({
                mode: "error",
                message:
                    err?.response?.data?.message ||
                    "Could not update prescriptions",
            });
        },
    });
}

export function useCreateDraftLabRequest() {
    const queryClient = useQueryClient();

    return useMutation<
        IApiResponse<ILabRequestMutationResponse>,
        AxiosError<IApiResponse<ILabRequestMutationResponse>>,
        ICreateDraftLabRequestPayload
    >({
        mutationKey: ["create-draft-lab-request"],
        mutationFn: (payload: ICreateDraftLabRequestPayload) =>
            createDraftLabRequest(payload),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["lab-requests"] });
            queryClient.invalidateQueries({
                queryKey: ["lab-request", response.data.lab_request_id],
            });
        },
    });
}

export function useUpdateLabRequest() {
    const queryClient = useQueryClient();

    return useMutation<
        IApiResponse<ILabRequest>,
        AxiosError<IApiResponse<ILabRequest>>,
        {
            labRequestId: string;
            payload: IEditDraftLabRequestPayload;
        }
    >({
        mutationKey: ["update-draft-lab-request"],
        mutationFn: ({ labRequestId, payload }) =>
            updateLabRequest(labRequestId, payload),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["lab-requests"] });
            queryClient.invalidateQueries({
                queryKey: ["lab-request", variables.labRequestId],
            });
        },
    });
}

export function useSignLabRequest() {
    const queryClient = useQueryClient();

    return useMutation<
        IApiResponse<ILabRequest>,
        AxiosError<IApiResponse<ILabRequest>>,
        {
            labRequestId: string;
            payload: ISignLabRequestPayload;
        }
    >({
        mutationKey: ["sign-draft-lab-request"],
        mutationFn: ({ labRequestId, payload }) =>
            signLabRequest(labRequestId, payload),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["lab-requests"] });
            queryClient.invalidateQueries({
                queryKey: ["lab-request", variables.labRequestId],
            });
        },
    });
}

export function useSendLabRequest() {
    return useMutation<
        { message: string },
        AxiosError<{ message: string }>,
        {
            labRequestId: string;
            payload: ISendLabRequestPayload;
        }
    >({
        mutationKey: ["send-lab-request"],
        mutationFn: ({ labRequestId, payload }) =>
            sendLabRequest(labRequestId, payload),
    });
}

export function useDownloadLabRequest() {
    return useMutation<Blob, AxiosError<string>, string>({
        mutationKey: ["download-lab-request"],
        mutationFn: downloadLabRequest,
        onSuccess: (data) => {
            const href = window.URL.createObjectURL(data);
            const anchorElement = document.createElement("a");

            anchorElement.href = href;
            anchorElement.download = "Lab-request.pdf";
            document.body.appendChild(anchorElement);
            anchorElement.click();
            anchorElement.remove();
            window.URL.revokeObjectURL(href);
        },
    });
}
